"use client";

import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn, platformProps } from "../../lib";
import { touchTargetClassName } from "../../lib/touch-target";
import type { SwitchPrimitiveProps } from "./types";

/**
 * Switch - Web implementation (Radix)
 *
 * Unstyled primitive. Styling is applied by the atom layer via className/thumbClassName.
 * When the browser reports a coarse primary pointer, its tap area grows to at least
 * 44×44px (`touchTargetClassName`).
 */
const Switch = React.forwardRef<
  React.ComponentRef<typeof SwitchPrimitive.Root>,
  SwitchPrimitiveProps
>(
  (
    {
      checked,
      onCheckedChange,
      disabled = false,
      className,
      thumbClassName,
      // Native only: web draws the halo as the thumb's ring
      haloClassName: _haloClassName,
      testID,
      id,
      accessibilityLabel,
      ...rest
    },
    ref,
  ) => (
    <SwitchPrimitive.Root
      ref={ref}
      {...platformProps({ testID, slot: "switch", id })}
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      disabled={disabled}
      aria-label={accessibilityLabel}
      className={cn(touchTargetClassName, className)}
      {...rest}
    >
      <SwitchPrimitive.Thumb className={thumbClassName} />
    </SwitchPrimitive.Root>
  ),
);

Switch.displayName = "Switch";

export { Switch };
